import {Button, Col, Form, Row, Spinner} from 'react-bootstrap'
import { DateInput } from 'app/tfi/components/DateInput'
import {TFormAllValues, TFormKeys} from '../policiesTypes'
import Select from 'react-select'
import {LazyQueryExecFunction, useQuery} from '@apollo/client'
import {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {GET_ALL_CARRIERS_LIST} from '../../../gql/queries/carriersQuery'
import {IResCarriersList} from '../../../types/carriers'
import {IResAllUsers} from '../../oneUser/oneUserTypes'
import {useDebounce} from '../../../../../_metronic/helpers'
import {IResPolicyTypeCategories} from '../../../types/policies'
import {IResLeadSource} from '../../leadSource/leadSourceTypes'
import {FormikErrors, FormikTouched} from 'formik'
import {parseId} from '../../../helpers'
import {useGetUser} from '../../../store/userStore'

export const BasicPolicyInfoTab: React.FC<{
  onPrev: () => void
  onSubmit: () => void
  handleChange: (field: string, value: any) => void
  handleBlur: (field: TFormKeys) => void
  values: TFormAllValues
  errors: FormikErrors<TFormAllValues>
  touched: FormikTouched<TFormAllValues>
  loading: boolean
  policiesType: IResPolicyTypeCategories | undefined
  leadSources: IResLeadSource | undefined
  dataUsers: IResAllUsers | undefined
  gqlGetMembers: LazyQueryExecFunction<IResAllUsers, {name: string; requireNpn: boolean}>
  loadingUsers: boolean
  isQuote?: boolean
  lockPolicyType?: boolean
  lockLeadSource?: boolean
}> = ({
  onPrev,
  onSubmit,
  isQuote,
  handleChange,
  handleBlur,
  loading: loadingCreate,
  values,
  errors,
  touched,
  policiesType,
  leadSources,
  gqlGetMembers,
  loadingUsers,
  dataUsers,
  lockPolicyType = false,
  lockLeadSource = false,
}) => {
  const primaryGroup = useGetUser((s) => s.user?.user?.primaryGroup)
  const isAgent = primaryGroup?.name === 'Agente'
  const currentUser = useGetUser((s) => s.user?.user)

  const {
    data: dataCarriers,
    loading: loadingCarriers,
    error: errorCarriers,
  } = useQuery<IResCarriersList>(GET_ALL_CARRIERS_LIST)

  const [inputSelect, setInputSelect] = useState('')
  const valueDeb = useDebounce(inputSelect, 500)
  const [options, setOptions] = useState<
    {value: string; labelName: string; email: string; npn?: string}[]
  >([])
  // Track selected agent for react-select
  const [selectedAgent, setSelectedAgent] = useState<{
    value: string
    labelName: string
    email: string
    npn?: string
  } | null>(null)
  const [autoSelectedCurrentUser, setAutoSelectedCurrentUser] = useState(false)
  const userId = values.user?.id
  const userLabel = String((values as any)?.userLabel || '')
  const userEmail = String((values as any)?.userEmail || '')
  const userNpn = String((values as any)?.userNpn || '')

  // Auto-search and select current user on mount
  useEffect(() => {
    if (!autoSelectedCurrentUser && !userId && currentUser?.id && currentUser.firstName) {
      // Trigger search with current user's first name to find them
      setInputSelect(currentUser.firstName)
    }
  }, [autoSelectedCurrentUser, currentUser, userId])

  useEffect(() => {
    if (!valueDeb) {
      return
    }
    if (!dataUsers) return
    const opts = dataUsers.allUsers.edges.map(({node}) => ({
      value: node.id,
      labelName: [node.firstName, node.lastName].filter(Boolean).join(' '),
      email: node.email || '',
      npn: (node as any).npn || (node as any).NPN || '',
    }))
    setOptions(opts)

    // Auto-select current user if they appear in the list and no user is selected yet
    if (!autoSelectedCurrentUser && !userId && currentUser?.id) {
      const currentUserInList = opts.find((opt) => parseId(opt.value) === parseId(currentUser.id))
      if (currentUserInList) {
        setSelectedAgent(currentUserInList)
        handleChange('user', {id: currentUserInList.value})
        handleChange('userLabel' as any, currentUserInList.labelName)
        handleChange('userEmail' as any, currentUserInList.email)
        handleChange('userNpn' as any, currentUserInList.npn)
        setAutoSelectedCurrentUser(true)
        // Clear the input text so it shows the selected option properly
        setInputSelect('')
      }
    }
  }, [autoSelectedCurrentUser, currentUser?.id, dataUsers, handleChange, userId, valueDeb])

  // Hydrate selected agent from Formik values if option not present
  useEffect(() => {
    // If we have a user selected in formik and it's not in options, synthesize it from formik (if we stored label/email)
    if (userId && !options.find((o) => String(o.value) === String(userId))) {
      if (userLabel) {
        const opt = {
          value: String(userId),
          labelName: userLabel,
          email: userEmail,
          npn: userNpn,
        }
        setOptions((prev) => [opt, ...prev])
        if (!selectedAgent) setSelectedAgent(opt)
      }
    }
  }, [options, selectedAgent, userEmail, userId, userLabel, userNpn])

  useEffect(() => {
    if (!valueDeb) return
    gqlGetMembers({
      variables: {
        name: valueDeb,
        requireNpn: true,
      },
    })
  }, [gqlGetMembers, valueDeb])

  useEffect(() => {
    if (errorCarriers) toast.error(`Error: ${errorCarriers.message}`)
  }, [errorCarriers])

  useEffect(() => {
    console.log('Formik → user.id:', values.user?.id)
  }, [values.user?.id])
  return (
    <>
      <div className='title-section-policy-form'>
        <h6 className='d-flex align-items-center gap-2'>
          Basic {isQuote ? 'quote' : 'policy'} information{' '}
        </h6>
      </div>
      <Row>
        <Col md={6} className="mb-5">
          <Form.Group>
            <Form.Label>
              Policy type <span style={{ color: 'red' }}>*</span>
              {errors.typePolicy?.id && (
                <small className="text-danger ms-2">{errors.typePolicy.id}</small>
              )}
            </Form.Label>
            <Form.Select
              aria-label="Policy type"
              name="typePolicy"
              required
              value={values.typePolicy?.id ? parseId(values.typePolicy.id) : ''}
              onChange={(e) => handleChange('typePolicy.id', e.target.value)}
              disabled={lockPolicyType}
            >
              <option value="">Please select</option>
              {policiesType?.allCategoryPolicy.edges.map(({ node }) => (
                <optgroup label={node.name} key={node.id}>
                  {node.policyTypeSet.edges.map(({ node: typeNode }) => (
                    <option key={typeNode.id} value={parseId(typeNode.id)}>
                      {typeNode.typePolicy}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        {!isAgent && (
          <Col className='col-lg-4 col-md-4 col-12 mb-4'>
            <Form.Group>
              <Form.Label>
                Source <span style={{ color: 'red' }}>*</span>
                {errors.leadSource?.id && (
                  <small className="text-danger ms-2">{errors.leadSource.id}</small>
                )}
              </Form.Label>
              <Form.Select
                aria-label="Source"
                name="leadSource"
                value={values.leadSource?.id ? parseId(values.leadSource.id) : ''}
                onChange={(e) => handleChange('leadSource.id', e.target.value)}
                disabled={lockLeadSource}
              >
                <option value="">Please select</option>
                {leadSources?.allLeadSources.edges.map(({ node }) => (
                    <option key={node.id} value={parseId(node.id)}>
                      {node.name}
                    </option>
                  ))}
              </Form.Select>
            </Form.Group>
          </Col>
        )}
        <Col md={6} className='mb-5'>
          <Form.Group>
            <Form.Label>Carrier</Form.Label>
            {loadingCarriers && <Spinner animation='border' size='sm' />}
            <Form.Select
              onChange={(e) => handleChange('carrier.id', e.target.value)}
              value={values.carrier.id}
            >
              <option value=''>Select one</option>
              {dataCarriers?.allInsuranceOperatorsList.map((carrier) => (
                <option key={carrier.id} value={carrier.id}>
                  {carrier.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6} className='mb-5'>
          <DateInput
            id="effectiveDate"
            name="effectiveDate"
            label="Effective date"
            value={values.effectiveDate}
            error={errors.effectiveDate as string}
            touched={touched.effectiveDate as boolean}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Col>
        <Col md={6} className='mb-5'>
          <Form.Group>
            <Form.Label>
              Who is the agent owner for this client?{' '}
              {touched.user?.id && errors.user?.id && (
                <small className='text-danger'>{errors.user.id}</small>
              )}
            </Form.Label>
            <Select
              isLoading={loadingUsers}
              loadingMessage={() => 'loading members'}
              placeholder='Search and select an agent...'
              options={options}
              inputValue={inputSelect}
              onInputChange={setInputSelect}
              getOptionValue={(opt) => String(opt.value)}
              getOptionLabel={(opt) =>
                `${opt.labelName} — ${opt.email}${opt.npn ? ` (${opt.npn})` : ''}`
              }
              formatOptionLabel={(opt) => (
                <div style={{display: 'flex', flexDirection: 'column'}}>
                  <span>{opt.labelName}</span>
                  <small style={{opacity: 0.8}}>
                    {opt.email}
                    {opt.npn ? ` (${opt.npn})` : ''}
                  </small>
                </div>
              )}
              value={
                selectedAgent ||
                options.find((o) => String(o.value) === String(values.user?.id)) ||
                null
              }
              onChange={(selectedOption) => {
                const userId = selectedOption?.value || ''
                const labelName = selectedOption?.labelName || ''
                const email = selectedOption?.email || ''
                const npn = selectedOption?.npn || ''
                setSelectedAgent(selectedOption || null)
                handleChange('user', {id: userId})
                // persist display fields for hydration on remount
                handleChange('userLabel' as any, labelName)
                handleChange('userEmail' as any, email)
                handleChange('userNpn' as any, npn)
              }}
            />
          </Form.Group>
        </Col>
      </Row>
      <div className='btn-prev-next'>
        <Button size='sm' variant='outline' onClick={onPrev}>
          PREVIOUS
        </Button>
        <Button size='sm' onClick={onSubmit} disabled={loadingCreate}>
          {loadingCreate && <Spinner animation='border' size='sm' />}
          SAVE
        </Button>
      </div>
    </>
  )
}
